import { useMemo, useState, useCallback } from "react";
import { toast, useAuth } from "@jana-technology/platform-ui";

import type { SelectOption } from "components/selects/types";
import { captureResponseError, throwResponseErrorAsync } from "helpers/errors";
import { sanitizeFilename } from "helpers/filenames";
import { tryParseContentDispositionFilename } from "helpers/parsers";

import { ExportContext } from "./context";
import { buildExportFilename } from "./export-filename";
import type { ExportContextValue } from "./types";

const KEY = import.meta.env.PLATFORM_AUTH_KEY as string;
const API_URL = import.meta.env.API_BASE_URL as string;

export const ExportProvider = ({ children }: React.PropsWithChildren) => {
  const { getTokenSilently } = useAuth();

  // might need to init with loading or something
  const [selectedReportType, setSelectedReportType] = useState<string>("");

  // these will come from the backend
  // first version only has report types; field selection comes later
  const { reportTypes } = useMemo(() => {
    return {
      reportTypes: [
        { value: "System Overview", children: "System Overview" },
        { value: "Scenario-Specific", children: "Scenario-Specific" },
      ] satisfies Array<SelectOption>,
    };
  }, []);

  const fetchExportData = useCallback<ExportContextValue["fetchExportData"]>(
    async (reportType, fieldIds) => {
      try {
        if (!reportType) {
          toast.warning("Please select a report type.");

          return;
        }

        if (fieldIds.length === 0) {
          toast.warning("Please select at least one field.");

          return;
        }

        toast.info("Export in progress...");

        const token = await getTokenSilently();

        const response = await fetch(
          `${API_URL}/data/v1/data/export?product=Lighthouse%20Storage&exportName=${encodeURIComponent(reportType)}`,
          {
            method: "POST",
            body: JSON.stringify(fieldIds),
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "Ocp-Apim-Subscription-Key": KEY,
            },
          },
        );

        if (!response.ok) {
          await throwResponseErrorAsync("Export response is not ok", response);
        }

        const fallbackFilename = buildExportFilename(reportType);
        const filename = sanitizeFilename(
          tryParseContentDispositionFilename(
            response.headers,
            fallbackFilename,
          ),
          fallbackFilename,
        );

        const blob = await response.blob();

        const url = globalThis.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        a.remove();

        globalThis.setTimeout(() => {
          globalThis.URL.revokeObjectURL(url);
        }, 0);

        toast.success(filename, {
          title: "Export successful",
        });
      } catch (error) {
        toast.error("Export failed. Please try again.");

        captureResponseError(error as Error, {
          tags: {
            component: "ExportProvider",
            caller: "fetchExportData",
          },
        });
      }
    },
    [getTokenSilently],
  );

  const value = useMemo<ExportContextValue>(
    () => ({
      selectedReportType,
      setSelectedReportType,
      reportTypes,
      fetchExportData,
    }),
    [selectedReportType, reportTypes, fetchExportData],
  );

  return (
    <ExportContext.Provider value={value}>{children}</ExportContext.Provider>
  );
};

export default ExportProvider;
